import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroSlider from '../components/HeroSlider';
import AdvertisementTicker from '../components/AdvertisementTicker';
import useAuthStore from '../store/authStore';
import { ShoppingCartIcon, UserIcon } from '@heroicons/react/24/outline';

const Home = () => {
  const [homeContent, setHomeContent] = useState({ sliders: [], advertisements: [] });
  const [loading, setLoading] = useState(true);
  const { user, userType, logout } = useAuthStore();
  const navigate = useNavigate();

  const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : process.env.REACT_APP_API_URL;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const fetchHomeContent = async () => {
      try {
        const response = await fetch(`${API_URL}/home-content`);
        if (response.ok) {
          const data = await response.json();
          setHomeContent(data);
        }
      } catch (error) {
        console.error('Error fetching home content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeContent();
  }, [API_URL]);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-slate-900 font-sans selection:bg-amber-100 flex flex-col justify-between">

      {/* BACKGROUND LAYER */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full">
          <HeroSlider sliders={homeContent.sliders} className="h-full w-full object-cover" />
        </div>
        {/* Overlay REMOVED as per user request for clear slider visibility */}
      </div>

      {/* TOP HEADER SECTION */}
      <div className="relative z-50 w-full flex flex-col">
        {/* Top Info Bar - Glassmorphism Applied */}
        <div className="w-full bg-slate-900/40 backdrop-blur-md text-white py-2.5 relative z-[60] border-b border-white/10 flex justify-center items-center px-4">

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-[10px] md:text-xs font-bold uppercase tracking-widest text-shadow-sm">
            {/* Special Offers */}
            <span className="flex items-center space-x-2 text-amber-400 drop-shadow-md">
              <span className="text-sm">✨</span>
              <span>Special Offers</span>
            </span>

            <span className="text-white/90 hidden sm:block drop-shadow-md">|</span>
            <span className="text-white/90 hover:text-amber-400 transition-colors cursor-pointer hidden md:block drop-shadow-md">Shop Tamil Products</span>

            <span className="text-white/90 hidden sm:block drop-shadow-md">|</span>
            <span className="text-white/90 hover:text-amber-400 transition-colors cursor-pointer hidden md:block drop-shadow-md">Join Business Network</span>

            {/* Auth Links (Integrated for Centered Layout) */}
            <span className="text-white/90 hidden sm:block drop-shadow-md">|</span>
            {!user && (
              <Link to="/about" className="text-white/90 hover:text-amber-400 transition-colors drop-shadow-md">
                About
              </Link>
            )}

            {userType === 'user' && (
              <Link to="/cart" className="text-white/90 hover:text-amber-400 transition-colors flex items-center drop-shadow-md">
                <ShoppingCartIcon className="h-4 w-4" />
              </Link>
            )}

            {user ? (
              <div className="relative group inline-block">
                <button className="flex items-center space-x-1 font-medium text-white/90 hover:text-amber-400 transition-colors uppercase tracking-wider drop-shadow-md">
                  <UserIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.name || user.businessName || user.phone}</span>
                </button>
                <div className="absolute right-0 top-full mt-2 w-40 bg-white text-slate-900 rounded shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] text-left">
                  {userType === 'user' && (
                    <Link to="/orders" className="block px-4 py-2 hover:bg-amber-50 hover:text-amber-600">My Orders</Link>
                  )}
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-2 hover:bg-red-50 hover:text-red-600">Logout</button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-amber-400 hover:text-white transition-colors border-b border-transparent hover:border-amber-400 drop-shadow-md"
              >
                Log in
              </Link>
            )}
          </div>
        </div>

        {/* Navbar - Passing hideRightMenu prop */}
        <Navbar customClass="relative bg-transparent z-[50]" hideRightMenu={true} hideLogo={true} />
      </div>

      {/* MIDDLE SECTION (CTAs) */}
      <div className="relative z-40 flex-grow flex items-center justify-center pb-24">
        <div className="flex flex-col md:flex-row gap-8 animate-fade-in-up items-center">

          {/* Call To Action Buttons - Enhanced Glassmorphism & High Visibility */}
          <Link
            to="/supplier/register"
            className="group relative px-10 py-5 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 shadow-[0_8px_32px_rgba(245,158,11,0.3)] border border-white/20 hover:border-amber-500/50"
          >
            {/* Glass Backdrop */}
            <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"></div>
            {/* Vivid Amber Gradient (Semi-transparent for glass feel but distinct) */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-600/90 to-orange-600/90 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="relative flex items-center space-x-3 text-white font-bold text-lg tracking-widest uppercase z-10 drop-shadow-md">
              <span>Become a Vendor</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
            </div>
          </Link>

          <Link
            to="/products"
            className="group relative px-10 py-5 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 shadow-[0_8px_32px_rgba(59,130,246,0.3)] border border-white/20 hover:border-blue-500/50"
          >
            {/* Glass Backdrop */}
            <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"></div>
            {/* Vivid Blue Gradient (Semi-transparent for glass feel but distinct) */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-700/90 to-indigo-700/90 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="relative flex items-center space-x-3 text-white font-bold text-lg tracking-widest uppercase z-10 drop-shadow-md">
              <span>Product Search</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            </div>
          </Link>
        </div>
      </div>

      {/* BOTTOM SECTION (Ticker) */}
      <div className="relative z-50 w-full mb-0">
        {/* Header Pill */}
        <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-slate-900/90 backdrop-blur-md text-amber-500 px-6 py-1 rounded-full shadow-lg border border-white/10">
            <h3 className="font-bold text-xs md:text-sm tracking-wider uppercase">Featured Businesses</h3>
          </div>
        </div>

        {/* Ticker Component */}
        <div className="bg-slate-900/70 backdrop-blur-xl border-t border-white/10 pt-6 pb-2">
          <AdvertisementTicker ads={homeContent.advertisements} />
        </div>
      </div>

    </div>
  );
};

export default Home;